/* Northsheep — switch the main product image on thumbnail hover.

   Clicking a thumbnail is a decision; hovering one is a glance. On a shop
   where five photographs of the same hide are the whole argument, making the
   shopper click five times to see them is friction with no purpose — the same
   reasoning behind hovering a size pill to see its price on the cards.

   It drives Dawn's own thumbnail button rather than touching the media list,
   so everything that follows a real click still happens: the active class,
   aria-current, and the variant-image logic. Nothing here knows how the
   gallery works.

   Only on real pointers. `(hover: hover)` keeps it away from phones, where a
   tap would otherwise fire hover and click together and switch twice.

   Three rules keep it calm (2026-09-16, after the owner found it twitchy):

   1. It listens for `mousemove`, not `mouseover`. Scrolling the page moves
      the strip under a resting cursor, and the browser reports that as a
      hover — so the big image used to change while the visitor was only
      reading their way down the page. A mouse that has not moved fires no
      mousemove, so a scroll can no longer switch anything.
   2. 150ms of rest on one thumbnail before it switches, so sweeping the
      cursor across the strip on the way somewhere else changes nothing.
   3. Hovering never scrolls the thumbnail strip. Dawn scrolls the active
      thumbnail into view, which slides a different thumbnail under the
      cursor and switches the image again — a chain reaction from one glance.
      A real click still scrolls, because that is a decision.

   Revert: Theme settings -> uncheck "Switch image when hovering a thumbnail".
*/
(function () {
  'use strict';

  /* DECIDED PER POINTER, NOT PER DEVICE (audit E4, 2 Oct 2026). The old
     `(hover: hover)` gate switched hovering off on any computer that reports
     touch as its main input, even with a mouse in hand. Only a mouse moves the
     photos now, whatever the device says it is - the rule CLAUDE.md sets for
     the card size pills. */
  /* Once per page (audit, 23 Sep 2026). Everything below is delegated from
     the document, so one copy serves any gallery — but a colour swap
     (ns-colour-siblings.js) runs this file again, and every extra copy
     clicked the same thumbnail once more on each hover. */
  if (window.__nsThumbHover) return;
  window.__nsThumbHover = true;

  var DELAY = 150;
  var timer = 0;
  var pending = null;

  function cancel() {
    if (timer) {
      clearTimeout(timer);
      timer = 0;
    }
    pending = null;
  }

  /* Drive the button, then put the strip back where it was. Restoring is
     unconditional: with smooth scrolling the scroll has not started yet when
     we look, so testing whether it moved would miss it. Writing scrollLeft
     with `scroll-behavior: auto` cancels an animation already in flight. */
  function switchTo(btn) {
    var strip = btn.closest('.thumbnail-list');
    var left = strip ? strip.scrollLeft : 0;
    var top = strip ? strip.scrollTop : 0;

    // Read by ns-gallery-strip.js: a hovered thumbnail is already in view (audit E6).
    window.__nsThumbHoverAt = Date.now();
    btn.click();

    if (!strip) return;
    var previous = strip.style.scrollBehavior;
    strip.style.scrollBehavior = 'auto';
    strip.scrollLeft = left;
    strip.scrollTop = top;
    strip.style.scrollBehavior = previous;
  }

  var lastX = -1;
  var lastY = -1;
  document.addEventListener(
    window.PointerEvent ? 'pointermove' : 'mousemove',
    function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      /* A MOVE THAT DID NOT MOVE (audit E5, 2 Oct 2026). After a scroll under
         a still cursor the browser reports one (Chrome and WebKit both), so
         scrolling the page past the strip switched the photo to whatever
         thumbnail slid under the pointer. */
      if (e.clientX === lastX && e.clientY === lastY) return;
      lastX = e.clientX;
      lastY = e.clientY;
      var btn = e.target.closest && e.target.closest('.thumbnail-list__item .thumbnail');
      if (!btn) {
        /* Off the strip: drop anything waiting, so a switch cannot land
           after the cursor has left. */
        if (pending) cancel();
        return;
      }

      /* Already showing: nothing to do. */
      if (btn.getAttribute('aria-current') === 'true') {
        cancel();
        return;
      }
      /* Moving within the same thumbnail — let its timer keep running. */
      if (pending === btn) return;

      cancel();
      pending = btn;
      timer = setTimeout(function () {
        timer = 0;
        pending = null;
        /* Still under the cursor? The pointer may have moved on during the
           delay, and switching to a thumbnail nobody is looking at is the
           bug this guard exists to prevent. */
        if (!btn.matches(':hover')) return;
        switchTo(btn);
      }, DELAY);
    },
    { passive: true }
  );

  /* Leaving the strip drops any pending switch. The image stays where it was
     rather than snapping back — a gallery that reverts on mouseout makes it
     impossible to move from a thumbnail to the image you just chose. */
  document.addEventListener(
    'mouseout',
    function (e) {
      var btn = e.target.closest && e.target.closest('.thumbnail-list__item .thumbnail');
      if (btn && btn === pending) cancel();
    },
    { passive: true }
  );
})();
